import { useState } from 'react'
import { Fingerprint } from 'lucide-react'
import { apiFetch } from '@/lib/api/client'
import { formatDate, fingerprint } from '@/lib/utils'
import { StepUpButton } from '../StepUpButton'
import type { Credential } from '@/lib/auth/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export function CredentialRow({
  credential,
  onUpdated,
}: {
  credential: Credential
  onUpdated: () => void
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(credential.name ?? '')
  const [saving, setSaving] = useState(false)

  async function handleSaveName() {
    const trimmed = editName.trim()
    if (!trimmed || trimmed === credential.name) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const response = await apiFetch(`/auth/webauthn/credentials/${credential.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmed }),
      })
      if (!response.ok) {
        throw new Error((await response.text()) || 'Failed to rename')
      }
      onUpdated()
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="group flex items-center gap-4 px-4 py-3.5 border border-border rounded-md bg-card hover:border-primary/30 transition-colors">
      <div className="flex items-center justify-center size-10 rounded-md bg-primary/10 border border-primary/20 text-primary shrink-0">
        <Fingerprint className="size-5" strokeWidth={1.5} />
      </div>

      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName()
                if (e.key === 'Escape') setEditing(false)
              }}
              disabled={saving}
              autoFocus
              maxLength={64}
              className="h-8"
            />
            <Button size="sm" onClick={handleSaveName} disabled={saving}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="text-sm font-medium text-foreground">
            {credential.name ?? 'Security key'}
          </div>
        )}
        <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-muted-foreground">
          <span className="font-mono uppercase tracking-wider">
            {fingerprint(credential.credential_id)}
          </span>
          <span>Registered {formatDate(credential.created_at)}</span>
          {credential.last_used_at && (
            <span>Last used {formatDate(credential.last_used_at)}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
        {confirmDelete ? (
          <>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
              Remove?
            </span>
            <StepUpButton
              scope="credential.delete"
              onAction={async () => {
                const response = await apiFetch(
                  `/auth/webauthn/credentials/${credential.id}`,
                  { method: 'DELETE' },
                )
                if (!response.ok) {
                  throw new Error((await response.text()) || 'Failed to delete credential')
                }
              }}
              onSuccess={() => {
                setConfirmDelete(false)
                onUpdated()
              }}
              onError={() => setConfirmDelete(false)}
              variant="danger"
              size="sm"
            >
              Confirm
            </StepUpButton>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditName(credential.name ?? '')
                setEditing(true)
              }}
            >
              Rename
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
